import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppContext } from '../contexts/Storage';
import { useRecords } from '../hooks/useRecords';
import { TransactionFetchFilter } from '../models/TransactionFetchFilter';
import { transactionsService } from '../services/transactionsService';
import {
  getOnboarding,
  isChecklistVisible,
  isDemoSession,
  looksLikeFalseComplete,
  markOnboarding,
  setOnboarding,
  OnboardingState,
  ONBOARDING_UPDATED_EVENT,
} from './onboardingStorage';

type GettingStartedChecklistProps = {
  variant?: 'card' | 'embedded';
};

const GettingStartedChecklist: React.FC<GettingStartedChecklistProps> = ({
  variant = 'card',
}) => {
  const location = useLocation();
  const { user, token } = useAppContext();
  const { fetchRecords } = useRecords();
  const [state, setState] = useState<OnboardingState>(() => getOnboarding(user?.id));

  useEffect(() => {
    const sync = () => setState(getOnboarding(user?.id));
    sync();
    window.addEventListener(ONBOARDING_UPDATED_EVENT, sync);
    return () => window.removeEventListener(ONBOARDING_UPDATED_EVENT, sync);
  }, [user?.id, location.pathname]);

  useEffect(() => {
    if (!user?.id || !token || isDemoSession()) return;
    let cancelled = false;

    (async () => {
      const result = await fetchRecords();
      if (cancelled || !result.success || !result.data) return;

      const current = getOnboarding(user.id);
      const hasBooks = result.data.length > 0;
      let hasTxns = false;

      if (hasBooks && result.data[0]?.id) {
        const filter = new TransactionFetchFilter();
        filter.record_id = result.data[0].id;
        filter.limit = 1;
        filter.page = 1;
        const txnResult = await transactionsService.getTransactions(token, user.id as number, filter);
        const total = txnResult.data?.transactions?.total ?? 0;
        hasTxns = total > 0;
      }

      if (cancelled) return;

      if (looksLikeFalseComplete(current) && !hasTxns) {
        setState(
          setOnboarding(user.id, {
            namedBooks: hasBooks,
            addedEvent: false,
            sawList: false,
            firstRunTourDone: false,
            checklistDismissed: false,
          })
        );
        return;
      }

      if (hasTxns && !current.addedEvent) {
        setState(
          setOnboarding(user.id, {
            namedBooks: true,
            addedEvent: true,
            sawList: true,
            firstRunTourDone: true,
            checklistDismissed: true,
          })
        );
        return;
      }

      if (hasBooks && !current.namedBooks) {
        setState(markOnboarding(user.id, 'namedBooks'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, token, fetchRecords]);

  useEffect(() => {
    if (!user?.id || isDemoSession()) return;
    if (location.pathname === '/records/transactions' && getOnboarding(user.id).namedBooks) {
      setState(markOnboarding(user.id, 'sawList'));
    }
  }, [location.pathname, user?.id]);

  const visible = useMemo(() => {
    if (isDemoSession()) return false;
    if (!user) return false;
    return isChecklistVisible(state);
  }, [state, user]);

  if (!visible) return null;
  if (variant === 'card' && location.pathname === '/dashboard') return null;

  const dismiss = () => {
    setState(setOnboarding(user?.id, { checklistDismissed: true }));
  };

  const items = [
    {
      id: 'namedBooks',
      done: state.namedBooks,
      label: 'Name your books',
      to: '/records?setup=1',
    },
    {
      id: 'addedEvent',
      done: state.addedEvent,
      label: 'Add one sale or expense',
      to: '/records/transactions/new',
    },
    {
      id: 'sawList',
      done: state.sawList,
      label: 'See it on the list',
      to: '/records/transactions',
    },
  ];

  const doneCount = items.filter((item) => item.done).length;

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-dark">Getting started</p>
          <p className="text-xs text-grey_dark mt-0.5">
            {doneCount} of {items.length} done
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-xs text-grey_dark hover:text-dark"
          aria-label="Dismiss getting started"
        >
          Hide
        </button>
      </div>
      <ol className="mt-3 flex flex-col sm:flex-row gap-2">
        {items.map((item, index) => (
          <li key={item.id} className="flex-1">
            {item.done ? (
              <span className="flex items-center gap-2 text-sm text-grey_dark px-3 py-2 rounded-md bg-secondary_light">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full text-xs bg-primary text-white">
                  ✓
                </span>
                <span className="line-through">{item.label}</span>
              </span>
            ) : (
              <Link
                to={item.to}
                className="flex items-center gap-2 text-sm text-dark px-3 py-2 rounded-md border border-grey_light hover:border-primary"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full text-xs bg-grey_light text-grey_dark">
                  {index + 1}
                </span>
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </>
  );

  if (variant === 'embedded') {
    return <div className="px-6 py-4 border-b border-grey_light">{body}</div>;
  }

  return (
    <div className="ml-2 mt-2 mb-2 rounded-lg bg-light border border-grey_light px-4 py-3">
      {body}
    </div>
  );
};

export default GettingStartedChecklist;
